import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import {
  CircleNotch, MagnifyingGlass, ArrowClockwise, EnvelopeSimple,
  Phone, Briefcase, ArrowRight, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PageAdvice from "../components/PageAdvice";

const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Sydney", dateStyle: "medium", timeStyle: "short" }) : "never";

function Avatar({ src, name, size = "h-12 w-12" }) {
  const initials = (name || "?").split(" ").map((x) => x[0]).filter(Boolean).join("").slice(0, 2).toUpperCase();
  return src
    ? <img src={src} alt="" className={`${size} rounded-sm object-cover`} />
    : <div className={`${size} flex items-center justify-center rounded-sm bg-[#0A0A0A] text-sm font-bold text-white`}>{initials}</div>;
}

export default function Technicians() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [tsState, setTsState] = useState(null);
  const [tsSyncing, setTsSyncing] = useState(false);

  const loadTsStatus = useCallback(async () => {
    try { const { data } = await api.get("/legacy/timesheets/status"); setTsState(data); return data; } catch { return null; }
  }, []);

  const load = useCallback(async () => {
    const { data } = await api.get("/technicians");
    setData(data);
    return data;
  }, []);

  useEffect(() => { load().catch(() => setData({ technicians: [], total: 0, index: {} })); loadTsStatus(); }, [load, loadTsStatus]);

  // Poll timesheet sync while running.
  useEffect(() => {
    if (!tsState?.running) return;
    const t = setInterval(() => loadTsStatus(), 5000);
    return () => clearInterval(t);
  }, [tsState?.running, loadTsStatus]);

  const syncTimesheets = async () => {
    setTsSyncing(true);
    try {
      const { data } = await api.post("/legacy/timesheets/sync");
      toast.message(data.message || "Timesheet sync started.");
      setTimeout(loadTsStatus, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start timesheet sync.");
    } finally { setTsSyncing(false); }
  };

  // Poll while a background sync is running.
  useEffect(() => {
    if (!data?.index?.syncing) return;
    const t = setInterval(() => load().catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [data?.index?.syncing, load]);

  const runSync = async () => {
    setSyncing(true);
    try {
      await api.post("/technicians/sync");
      await load();
    } finally { setSyncing(false); }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.technicians;
    return data.technicians.filter((t) => [t.name, t.position, t.email].join(" ").toLowerCase().includes(q));
  }, [data, query]);

  const idx = data?.index || {};

  return (
    <Layout>
      <div data-testid="technicians-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Team</p>
            <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Technicians</h1>
            <p className="mt-2 max-w-2xl text-base text-neutral-600">Every staff member from AroFlo. Open a technician to see their details and the jobs they're assigned to.</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex gap-2">
              <button data-testid="sync-timesheets" onClick={syncTimesheets} disabled={tsSyncing || tsState?.running}
                className="flex items-center gap-2 rounded-sm border border-[#0055FF] px-4 py-2.5 text-sm font-semibold text-[#0055FF] transition-all duration-200 hover:bg-[#0055FF]/5 disabled:opacity-50">
                {(tsSyncing || tsState?.running) ? <CircleNotch size={16} className="animate-spin" /> : <Clock size={16} weight="bold" />}
                {tsState?.running ? "Syncing timesheets…" : "Sync timesheets"}
              </button>
              <button data-testid="sync-technicians" onClick={runSync} disabled={syncing || idx.syncing}
                className="flex items-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-50">
                {(syncing || idx.syncing) ? <CircleNotch size={16} className="animate-spin" /> : <ArrowClockwise size={16} weight="bold" />}
                {idx.syncing ? "Indexing jobs…" : "Refresh job links"}
              </button>
            </div>
            <span className="text-xs text-neutral-400" data-testid="index-asof">
              Jobs indexed: {fmtTime(idx.as_of)}{tsState?.total ? ` · ${tsState.total.toLocaleString()} timesheets` : ""}
            </span>
          </div>
        </div>

        {data === null ? (
          <div className="mt-10 flex h-40 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
        ) : (
          <>
            <div className="relative mt-6 max-w-md">
              <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input data-testid="tech-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, role or email…"
                className="w-full rounded-sm border border-neutral-300 bg-white py-2.5 pl-11 pr-4 text-base outline-none focus:border-[#0055FF]" />
            </div>
            <p className="mt-3 text-sm text-neutral-500" data-testid="tech-count">{filtered.length} technicians</p>

            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((t) => (
                <button key={t.id} data-testid={`tech-card-${t.id}`} onClick={() => navigate(`/technicians/${t.id}`)}
                  className="group flex items-start gap-3 border border-neutral-200 bg-white p-4 text-left shadow-sm rounded-sm transition-all duration-200 hover:border-[#0055FF] hover:shadow-md">
                  <Avatar src={t.avatar} name={t.name} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-heading text-lg font-black tracking-tight text-[#0A0A0A]">{t.name}</p>
                    <p className="truncate text-sm text-neutral-500">{t.position || "—"}</p>
                    <p className="mt-1 flex items-center gap-1.5 truncate text-xs text-neutral-500"><EnvelopeSimple size={12} /> {t.email || "—"}</p>
                    {t.mobile && <p className="flex items-center gap-1.5 truncate text-xs text-neutral-500"><Phone size={12} /> {t.mobile}</p>}
                    <span className="mt-2 inline-flex items-center gap-1.5 rounded-sm bg-[#0055FF]/10 px-2 py-0.5 text-xs font-semibold text-[#0055FF]">
                      <Briefcase size={12} weight="bold" /> {t.job_count} recent job{t.job_count === 1 ? "" : "s"}
                    </span>
                  </div>
                  <ArrowRight size={16} className="mt-1 shrink-0 text-neutral-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-[#0055FF]" />
                </button>
              ))}
            </div>
          </>
        )}
        <PageAdvice items={[
          "The technician directory is synced from the Aroflo V2 API. \u201cRefresh job links\u201d re-indexes which recent jobs each person worked on.",
          "\u201cSync timesheets\u201d pulls labour entries from the Legacy Aroflo API into the Hub \u2014 run it after adding new timesheet data.",
          "Open a technician to see their timesheet totals and history. Entries are matched by exact Legacy user ID where available, otherwise by name.",
        ]} />
      </div>
    </Layout>
  );
}
